import { Monitor, Moon, Sun } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ThemePreference } from '../../../shared/types';
import { DropdownItem, DropdownMenu, Tooltip } from '../ui';
import { useSessionStore } from '../../stores/session';
import { cn } from '../../lib/utils';

const OPTIONS: Array<{ value: ThemePreference; label: string; icon: LucideIcon }> = [
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'system', label: 'System', icon: Monitor },
];

export interface ThemeToggleProps {
  className?: string;
  /** `menu` gives the three explicit choices; `switch` flips dark ⇄ light. */
  mode?: 'menu' | 'switch';
}

/**
 * Appearance control. Dark is the default and the design is built for it, but
 * light is a first-class theme — every token has both values.
 */
export function ThemeToggle({ className, mode = 'menu' }: ThemeToggleProps) {
  const theme = useSessionStore((state) => state.theme);
  const setTheme = useSessionStore((state) => state.setTheme);
  const active = OPTIONS.find((option) => option.value === theme) ?? OPTIONS[0]!;
  const ActiveIcon = active.icon;

  const triggerClasses = cn(
    'inline-flex size-9 items-center justify-center rounded-md text-muted transition-colors',
    'hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
    className,
  );

  if (mode === 'switch') {
    const next = theme === 'dark' ? 'light' : 'dark';
    return (
      <Tooltip content={`Switch to ${next} mode`}>
        <button
          type="button"
          aria-label={`Switch to ${next} mode`}
          onClick={() => setTheme(next)}
          className={triggerClasses}
        >
          <ActiveIcon className="size-4" aria-hidden />
        </button>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu
      align="end"
      menuClassName="min-w-40"
      trigger={
        <button type="button" aria-label={`Theme: ${active.label}`} className={triggerClasses}>
          <ActiveIcon className="size-4" aria-hidden />
        </button>
      }
    >
      {(close) => (
        <>
          {OPTIONS.map((option) => (
            <DropdownItem
              key={option.value}
              icon={<option.icon />}
              active={option.value === theme}
              onSelect={() => {
                setTheme(option.value);
                close();
              }}
            >
              {option.label}
            </DropdownItem>
          ))}
        </>
      )}
    </DropdownMenu>
  );
}
